import * as React from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Person24Regular,
  Settings24Regular,
  SignOut24Regular,
} from "@fluentui/react-icons";
import { zegalColors } from "../theme";
import {
  Settings as SettingsModel,
  isAuthConfigured,
  loadSettings,
  saveSettings,
} from "../../zegal/settings";
import { DoctypeListItem } from "../../zegal/types";
import { exchangeApiKey } from "../../zegal/api";
import SettingsPanel from "./Settings";
import TemplateList from "./TemplateList";
import TemplateDetail from "./TemplateDetail";
import LoginPanel from "./Login";

type View = { kind: "list" } | { kind: "detail"; item: DoctypeListItem } | { kind: "settings" };

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  brandLogo: {
    height: "24px",
    width: "auto",
    color: zegalColors.brand,
  },
  brandText: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: zegalColors.textPrimary,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  body: { flex: 1, display: "flex", flexDirection: "column" },
});

const App: React.FC = () => {
  const styles = useStyles();
  const [settings, setSettings] = React.useState<SettingsModel>(() => loadSettings());
  const [view, setView] = React.useState<View>({ kind: "list" });
  const authed = isAuthConfigured(settings);

  const handleSignOut = React.useCallback(() => {
    // Full sign-out: clear the cached Dragonball *and* the API key, so the
    // user is dropped back to the login form.
    const next: SettingsModel = { ...settings, dragonball: "", username: "", apiKey: "" };
    saveSettings(next);
    setSettings(next);
    setView({ kind: "list" });
  }, [settings]);

  // When an authenticated request returns 401, the Dragonball is stale.
  // If we have a stored API key, exchange it for a fresh Dragonball
  // transparently. Otherwise sign the user out.
  const handleUnauthorized = React.useCallback(async () => {
    if (settings.apiKey) {
      try {
        const { dragonball, username } = await exchangeApiKey(settings, settings.apiKey);
        const next: SettingsModel = { ...settings, dragonball, username: username || settings.username };
        saveSettings(next);
        setSettings(next);
        return;
      } catch {
        // fall through to sign-out
      }
    }
    handleSignOut();
  }, [settings, handleSignOut]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.brand}>
          <img src="assets/zegal-logo.svg" alt="Zegal" className={styles.brandLogo} />
          <span className={styles.brandText}>Templates</span>
        </div>
        <div className={styles.headerActions}>
          {authed && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="subtle"
                  icon={<Person24Regular />}
                  aria-label={settings.username || "Account"}
                />
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem disabled>{settings.username || "Signed in"}</MenuItem>
                  <MenuItem icon={<SignOut24Regular />} onClick={handleSignOut}>
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
          <Button
            appearance={view.kind === "settings" ? "primary" : "subtle"}
            icon={<Settings24Regular />}
            onClick={() =>
              setView(view.kind === "settings" ? { kind: "list" } : { kind: "settings" })
            }
            aria-label="Settings"
          />
        </div>
      </div>

      <div className={styles.body}>
        {view.kind === "settings" ? (
          <SettingsPanel
            settings={settings}
            onSave={(next) => {
              setSettings(next);
              setView({ kind: "list" });
            }}
            onCancel={() => setView({ kind: "list" })}
          />
        ) : !authed ? (
          <LoginPanel
            settings={settings}
            onSignedIn={(next) => {
              setSettings(next);
              setView({ kind: "list" });
            }}
          />
        ) : view.kind === "list" ? (
          <TemplateList
            settings={settings}
            onSelect={(item) => setView({ kind: "detail", item })}
            onUnauthorized={handleUnauthorized}
          />
        ) : (
          <TemplateDetail
            settings={settings}
            item={view.item}
            onBack={() => setView({ kind: "list" })}
            onUnauthorized={handleUnauthorized}
          />
        )}
      </div>
    </div>
  );
};

export default App;
